/**
 * ExpansionMenu - Dropdown menu for lumped node expansion
 *
 * Appears when clicking a lumped node, showing options to expand
 * and a list of contained accounts with their balances.
 */

import { memo, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { LumpedNode } from './types';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';

export interface ExpansionMenuProps {
  node: LumpedNode;
  position: { x: number; y: number };
  onExpand: () => void;
  onAccountClick?: (accountId: string) => void;
  onClose: () => void;
  className?: string;
}

/**
 * Margin from viewport edges
 */
const VIEWPORT_MARGIN = 10;

/**
 * Menu dimensions
 */
const MENU_WIDTH = 280;
const MENU_MAX_HEIGHT = 360;

function ExpansionMenuComponent({
  node,
  position,
  onExpand,
  onAccountClick,
  onClose,
  className,
}: ExpansionMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    // Add listeners with a small delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Calculate adjusted position to stay within viewport
  const getAdjustedPosition = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let x = position.x;
    let y = position.y;

    // Constrain to right edge
    if (x + MENU_WIDTH > viewportWidth - VIEWPORT_MARGIN) {
      x = viewportWidth - MENU_WIDTH - VIEWPORT_MARGIN;
    }

    // Constrain to left edge
    if (x < VIEWPORT_MARGIN) {
      x = VIEWPORT_MARGIN;
    }

    // Constrain to bottom edge
    if (y + MENU_MAX_HEIGHT > viewportHeight - VIEWPORT_MARGIN) {
      y = viewportHeight - MENU_MAX_HEIGHT - VIEWPORT_MARGIN;
    }

    // Constrain to top edge
    if (y < VIEWPORT_MARGIN) {
      y = VIEWPORT_MARGIN;
    }

    return { x, y };
  }, [position]);

  const adjustedPosition = getAdjustedPosition();

  const handleExpandClick = useCallback(() => {
    onExpand();
    onClose();
  }, [onExpand, onClose]);

  const handleAccountItemClick = useCallback(
    (accountId: string) => {
      onAccountClick?.(accountId);
      onClose();
    },
    [onAccountClick, onClose]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, action: () => void) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    },
    []
  );

  const menuElement = (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-50',
        'bg-white rounded-lg shadow-xl border border-gray-200',
        'animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-150',
        className
      )}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
        width: MENU_WIDTH,
        maxHeight: MENU_MAX_HEIGHT,
      }}
      role="menu"
      aria-label={`Options for ${node.name}`}
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">{node.name}</h3>
        <p className="text-sm text-gray-500 mt-0.5">
          {node.containedAccounts.length} account{node.containedAccounts.length !== 1 ? 's' : ''}
          {' '}&bull;{' '}
          {formatCurrency(node.totalBalance)} total
        </p>
      </div>

      {/* Expand action */}
      <div className="px-2 py-2 border-b border-gray-100">
        <button
          type="button"
          onClick={handleExpandClick}
          onKeyDown={(e) => handleKeyDown(e, handleExpandClick)}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2 rounded-md',
            'text-left text-sm font-medium',
            'text-primary-600 hover:bg-primary-50',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset',
            'transition-colors'
          )}
          role="menuitem"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
            />
          </svg>
          <span>Expand in diagram</span>
        </button>
      </div>

      {/* Account list */}
      <div
        className="overflow-y-auto"
        style={{ maxHeight: MENU_MAX_HEIGHT - 140 }}
        role="group"
        aria-label="Contained accounts"
      >
        {node.containedAccounts.map((account) => (
          <button
            key={account.id}
            type="button"
            onClick={() => handleAccountItemClick(account.accountId)}
            onKeyDown={(e) => handleKeyDown(e, () => handleAccountItemClick(account.accountId))}
            className={cn(
              'w-full flex items-center justify-between px-4 py-2.5',
              'text-left text-sm',
              'hover:bg-gray-50',
              'focus:outline-none focus:bg-gray-50',
              'transition-colors',
              'border-b border-gray-50 last:border-b-0'
            )}
            role="menuitem"
          >
            <div className="flex items-center gap-3">
              {/* Account type icon */}
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center',
                  'bg-gray-100 text-gray-600'
                )}
              >
                <AccountTypeIcon type={account.accountType} />
              </div>
              <div>
                <div className="font-medium text-gray-900">{account.name}</div>
                <div className="text-xs text-gray-500 capitalize">
                  {account.accountType.replace('_', ' ')}
                </div>
              </div>
            </div>
            <div
              className={cn(
                'font-medium tabular-nums',
                account.balance < 0 ? 'text-red-600' : 'text-gray-900'
              )}
            >
              {formatCurrency(account.balance)}
            </div>
          </button>
        ))}
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50 rounded-b-lg">
        <p className="text-xs text-gray-500">
          Click an account to view its transactions
        </p>
      </div>
    </div>
  );

  // Render as portal to body
  return createPortal(menuElement, document.body);
}

/**
 * Icon component for account types
 */
function AccountTypeIcon({ type }: { type: string }) {
  switch (type) {
    case 'depository':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      );
    case 'credit':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      );
    case 'investment':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
          />
        </svg>
      );
    case 'loan':
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
    default:
      return (
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
          />
        </svg>
      );
  }
}

export const ExpansionMenu = memo(ExpansionMenuComponent);
export default ExpansionMenu;
